import { formatCurrencyForUser } from '@/utils/currencyUtilsV2';
import { formatDateTimeForUser } from '@/utils/dateUtilsV2';

const EMPTY_VALUE = '—';

/** Copy text to the clipboard. Returns true when successful. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Return the first characters of an identifier for compact displays. */
export function shortIdentifier(identifier: string, length = 8): string {
  return identifier ? identifier.substring(0, length) : '';
}

/** Format a date-time using the shared user-aware formatter. */
export function formatDateTime(
  value: string | Date | null | undefined,
  language = 'en',
  timezone?: string,
): string {
  if (!value) return EMPTY_VALUE;
  return formatDateTimeForUser(value, language, timezone) || EMPTY_VALUE;
}

/** Format a date relative to today. */
export function formatRelativeDate(value: string | Date | null | undefined): string {
  if (!value) return EMPTY_VALUE;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'in the future';
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/** Format a monetary amount using the shared user-aware formatter. */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency = 'EUR',
  language = 'en',
): string {
  if (amount == null || amount === '') return EMPTY_VALUE;

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return EMPTY_VALUE;

  return formatCurrencyForUser(numericAmount, currency, language) || EMPTY_VALUE;
}

/** Format a whole-number monetary amount (no decimals), e.g. "2.000 €". */
export function formatCurrencyWhole(
  amount: number | string | null | undefined,
  currency = 'EUR',
  locale = 'de-DE',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}
